import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useSearch } from "wouter";
import { X, Star, MapPin, ChevronLeft, ChevronRight, Navigation, LocateFixed, Search } from "lucide-react";
import { Capacitor } from "@capacitor/core";
// Static, not dynamic: a lazily-imported chunk 404s for any session that was
// open across a redeploy (trap #12), and this runs the moment the map opens.
import { Geolocation } from "@capacitor/geolocation";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { haversineMiles } from "@/lib/geo";
import { parseBrowseFilters, DISTANCE_OPTIONS, BROWSE_FILTERS_EVENT, type BrowseFilters } from "@/lib/browseFilters";
import { buildProducerIndex, matchesProducer, matchesAssist, type SearchAssist } from "@shared/searchFields";
import { BROWSE_CATEGORIES } from "@/components/CategoryChips";
import { useSwipePager } from "@/hooks/useSwipePager";

// How wide the map opens around the person looking at it. Framing only — the
// Distance filter is untouched, because the maker base is still sparse enough
// that a hard 25-mile cut shows an empty map to most of the country.
const OPEN_RADIUS_MILES = 25;
const METERS_PER_MILE = 1609.34;

type FixSource = "gps" | "approx" | "saved";

function countActiveFilters(f: BrowseFilters): number {
  return (
    (f.category ? 1 : 0) +
    (f.fulfillment !== "any" ? 1 : 0) +
    (f.withinMiles != null ? 1 : 0) +
    (f.showSoldOut ? 0 : 1) +
    (f.ebtOnly ? 1 : 0) +
    (f.q ? 1 : 0)
  );
}

interface MapPin {
  id: string;
  displayName: string;
  sellerType: string | null;
  profileImageUrl: string | null;
  city: string | null;
  state: string | null;
  averageRating: number;
  reviewCount: number;
  lat: number;
  lng: number;
  radiusMeters: number;
  // Facets so the map can honor the same filters as browse.
  categories?: string[];
  fulfillmentMethods?: string[];
  hasStock?: boolean;
}

interface SellerItem {
  id: string;
  name: string;
  imageUrl?: string | null;
  price?: string | number | null;
}

function money(v: string | number | null | undefined): string {
  if (v == null) return "";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) return "";
  return `$${n.toFixed(2)}`;
}

export function SellerMap({
  initialCenter,
  focusSellerId,
  onNavigate,
  onSheetHeightChange,
}: {
  initialCenter?: { latitude: number; longitude: number } | null;
  focusSellerId?: string | null;
  // Called when a link inside the map sheet is tapped, so the parent can close
  // the map. Fires on the tap itself (not on a location change) so it also
  // closes when the destination equals the page you're already on — e.g.
  // tapping the same maker whose profile you opened the map from.
  onNavigate?: () => void;
  // Measured height of the maker quick-view sheet (0 when nothing is selected)
  // so the map toggle can sit just above it instead of being covered. Measured
  // rather than assumed because the sheet grows with the listing strip.
  onSheetHeightChange?: (height: number) => void;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Layer[]>([]);
  const didFitRef = useRef(false);
  // Set when the filters change, consumed by the layer-building effect on the
  // next render — by then `pins` holds the set the new framing should cover.
  const refitPendingRef = useRef(false);
  const [selected, setSelected] = useState<MapPin | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const search = useSearch();

  // Where we think the viewer is, and how much to trust it. Only a real device
  // fix ("gps") gets a dot on the map — drawing one for a city-level IP guess
  // would tell people we know where they are when we don't.
  const [fix, setFix] = useState<
    { lat: number; lng: number; accuracy: number | null; source: FixSource } | null
  >(initialCenter ? { lat: initialCenter.latitude, lng: initialCenter.longitude, accuracy: null, source: "saved" } : null);
  const [locating, setLocating] = useState(false);
  // Set when the OS says location is off or refused, so we can explain it once.
  const [gpsProblem, setGpsProblem] = useState<"denied" | "off" | null>(null);
  const [promptDismissed, setPromptDismissed] = useState(false);
  // Dismissed for this session only (not persisted): the card comes back next
  // time the map opens, or once makers actually appear nearby.
  const [emptyDismissed, setEmptyDismissed] = useState(false);
  const [emptySwipe, setEmptySwipe] = useState(0);
  const emptyTouchStart = useRef<number | null>(null);
  const watchIdRef = useRef<string | null>(null);
  // The viewer moving the map is a decision; don't yank the view back to them
  // when a later, more accurate fix lands.
  const userMovedRef = useRef(false);
  // Quality of the fix the current framing was built on, so a better one can
  // replace it exactly once.
  const framedRankRef = useRef(-1);

  const applyFix = useCallback((next: { lat: number; lng: number; accuracy: number | null; source: FixSource }) => {
    setFix((current) => {
      // Never downgrade: a slow GPS fix must not be replaced by the IP guess
      // that resolved after it.
      if (current?.source === "gps" && next.source !== "gps") return current;
      return next;
    });
  }, []);

  const locate = useCallback(async () => {
    setLocating(true);
    try {
      // maximumAge MUST be 0 (trap #14): a cached fix satisfies the request
      // without consulting the OS Location Services toggle, so a device with
      // location switched off still looks fine. 12s because a cold fix indoors
      // on Android genuinely takes that long.
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      });
      applyFix({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
        source: "gps",
      });
      setGpsProblem(null);
      return true;
    } catch (err: any) {
      // PERMISSION_DENIED is worth wording differently from "services are off",
      // because the fix is in a different place on the device.
      const denied = err?.code === 1 || /denied|permission/i.test(String(err?.message || ""));
      setGpsProblem(denied ? "denied" : "off");
      // Fall back to the edge's city-level guess rather than stranding them on
      // a saved ZIP or the middle of the map.
      try {
        const res = await fetch("/api/geo/approx", { credentials: "include" });
        if (res.ok) {
          const geo = await res.json();
          if (geo?.available) {
            applyFix({ lat: geo.latitude, lng: geo.longitude, accuracy: null, source: "approx" });
          }
        }
      } catch {
        /* the saved centre already seeded `fix`; nothing further to do */
      }
      return false;
    } finally {
      setLocating(false);
    }
  }, [applyFix]);

  useEffect(() => {
    let cancelled = false;
    // Sequential, not concurrent. Firing the watch alongside the one-shot
    // request makes two simultaneous geolocation requests on the same frame,
    // and the one-shot loses: it returned "Timeout expired" every time with a
    // position sitting right there, which showed up as no dot and the map
    // framed on the stale saved centre. Get the fix, then keep it fresh.
    (async () => {
      const ok = await locate();
      if (cancelled || !ok) return;
      try {
        const id = await Geolocation.watchPosition(
          { enableHighAccuracy: true, timeout: 20000 },
          (pos, err) => {
            if (cancelled || err || !pos) return;
            applyFix({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
              source: "gps",
            });
          },
        );
        if (cancelled) void Geolocation.clearWatch({ id }).catch(() => {});
        else watchIdRef.current = id;
      } catch {
        /* the one-shot fix already placed us; a dead watch just means no updates */
      }
    })();
    return () => {
      cancelled = true;
      const id = watchIdRef.current;
      watchIdRef.current = null;
      if (id) void Geolocation.clearWatch({ id }).catch(() => {});
    };
  }, [locate, applyFix]);

  const [sheetH, setSheetH] = useState(0);
  useEffect(() => {
    if (!selected) {
      setSheetH(0);
      onSheetHeightChange?.(0);
      return;
    }
    const el = sheetRef.current;
    if (!el) return;
    const report = () => {
      const h = el.getBoundingClientRect().height;
      setSheetH(h);
      onSheetHeightChange?.(h);
    };
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const { data: allPins = [], isLoading } = useQuery<MapPin[]>({
    queryKey: ["/api/sellers/map"],
    queryFn: async () => {
      const res = await fetch("/api/sellers/map", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load map");
      return res.json();
    },
    staleTime: 60_000,
  });

  // Seeded from the URL so opening the map from a filtered list keeps that
  // filter, then held locally. Local rather than driven by useSearch because
  // the map is an overlay: editing the query string from here is a same-path
  // change, which doesn't reliably re-render in the wrapped WebView (trap #13),
  // and it would fight the history entry the map pushed to own its back button.
  // Changes are mirrored back into the URL with replaceState so browse picks
  // them up when the map closes.
  const [filters, setFilters] = useState<BrowseFilters>(() => parseBrowseFilters(search));
  const filtersRef = useRef(filters);
  const { category, fulfillment, showSoldOut, withinMiles, q } = filters;
  const [mapSearchOpen, setMapSearchOpen] = useState(false);

  const updateFilters = (patch: Partial<BrowseFilters>) => {
    const next = { ...filters, ...patch };
    // Re-frame on every filter change: narrowing zooms in around whatever is
    // left, and dropping the last filter pulls back out to every maker. The view
    // is otherwise fitted once on open and never again, which left you looking at
    // the old framing — zoomed out over pins that had just been hidden, or still
    // tight on one pin after un-hiding the rest.
    refitPendingRef.current = true;
    setFilters(next);
    filtersRef.current = next;
    // Announce as well as mirror to the URL. Closing the map pops the history
    // entry it pushed, which restores the URL from *before* these edits, so the
    // browse list can't learn about them from the query string alone.
    window.dispatchEvent(new CustomEvent(BROWSE_FILTERS_EVENT, { detail: next }));
    try {
      const params = new URLSearchParams(window.location.search);
      const setOrDelete = (key: string, value: string | null) => {
        if (value == null) params.delete(key);
        else params.set(key, value);
      };
      setOrDelete("category", next.category);
      setOrDelete("fulfillment", next.fulfillment === "any" ? null : next.fulfillment);
      setOrDelete("showSoldOut", next.showSoldOut ? null : "0");
      setOrDelete("within", next.withinMiles == null ? null : String(next.withinMiles));
      setOrDelete("ebt", next.ebtOnly ? "1" : null);
      setOrDelete("q", next.q || null);
      const qs = params.toString();
      // Keep the existing history state: it carries the map's own back-button
      // entry, and dropping it would strand the user on the map.
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${qs ? `?${qs}` : ""}`,
      );
    } catch {
      /* URL mirroring is a nicety; the map already updated from local state */
    }
  };

  // Closing the map pops the history entry it pushed, restoring the URL from
  // BEFORE this session — so a search cleared (or set) here would be resurrected
  // by anything that reads the URL. After the pop lands, replay the final
  // selection over both channels: the event for state, the URL for deep-linkers.
  useEffect(() => {
    return () => {
      const final = filtersRef.current;
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(BROWSE_FILTERS_EVENT, { detail: final }));
        if (window.location.pathname !== "/") return;
        try {
          const params = new URLSearchParams(window.location.search);
          const setOrDelete = (key: string, value: string | null) => {
            if (value == null) params.delete(key);
            else params.set(key, value);
          };
          setOrDelete("category", final.category);
          setOrDelete("fulfillment", final.fulfillment === "any" ? null : final.fulfillment);
          setOrDelete("showSoldOut", final.showSoldOut ? null : "0");
          setOrDelete("within", final.withinMiles == null ? null : String(final.withinMiles));
          setOrDelete("ebt", final.ebtOnly ? "1" : null);
          setOrDelete("q", final.q || null);
          const qs = params.toString();
          window.history.replaceState(
            window.history.state,
            "",
            `${window.location.pathname}${qs ? `?${qs}` : ""}`,
          );
        } catch {
          /* the event above already carried the state */
        }
      }, 120);
    };
  }, []);

  const activeFilterCount = countActiveFilters(filters);
  const clearFilters = () =>
    updateFilters({
      category: null,
      fulfillment: "any",
      showSoldOut: true,
      withinMiles: null,
      ebtOnly: false,
      q: "",
    });

  // A maker matches a search because of their name or what they SELL, so the
  // map needs the catalog to filter the same way the list does. Same query key
  // browse uses, so this is served from cache rather than refetched.
  const { data: searchItems = [] } = useQuery<
    { name: string; sellerId?: string | null; category?: string | null; isGlutenFree?: number | null }[]
  >({
    queryKey: ["/api/items", "all"],
    queryFn: async () => {
      const res = await fetch("/api/items", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: q !== "",
    staleTime: 60_000,
  });
  const producerIndex = useMemo(() => buildProducerIndex(searchItems as any), [searchItems]);

  // The same AI expansion the browse list uses, so "something for a picnic"
  // pins the makers who have picnic food instead of emptying the map. Served
  // from the endpoint's cache — browse already asked for this query.
  const [assist, setAssist] = useState<SearchAssist | null>(null);
  useEffect(() => {
    setAssist(null);
    if (q.length < 3) return;
    let cancelled = false;
    fetch("/api/search/assist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ q }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setAssist({
            terms: data.terms ?? [],
            dietary: data.dietary ?? [],
            categories: data.categories ?? [],
          });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [q]);

  const assistSellerIds = useMemo(() => {
    if (!assist) return null;
    const ids = new Set<string>();
    for (const item of searchItems as any[]) {
      if (item?.sellerId && matchesAssist(assist, item)) ids.add(item.sellerId);
    }
    return ids;
  }, [assist, searchItems]);

  // Distances — the radius ring, the Distance filter, "N miles away" — all
  // measure from wherever we currently believe the viewer is, so they update
  // when a real fix replaces the saved centre.
  const centerLat = fix?.lat ?? null;
  const centerLng = fix?.lng ?? null;
  // Memoized on the primitive filter values: the layer-building effect keys on
  // `pins`, so an unmemoized array would tear down and rebuild every Leaflet
  // marker on each render.
  const pins = useMemo(
    () =>
      allPins.filter((pin) => {
        if (category && !(pin.categories ?? []).includes(category)) return false;
        if (fulfillment !== "any" && !(pin.fulfillmentMethods ?? []).includes(fulfillment)) return false;
        if (!showSoldOut && pin.hasStock === false) return false;
        if (
          q &&
          !matchesProducer(q, pin as any, producerIndex) &&
          !(assistSellerIds?.has(pin.id) ?? false)
        ) {
          return false;
        }
        if (withinMiles != null) {
          if (centerLat == null || centerLng == null) return false;
          const miles = haversineMiles(
            { latitude: centerLat, longitude: centerLng },
            { latitude: pin.lat, longitude: pin.lng },
          );
          if (!Number.isFinite(miles) || miles > withinMiles) return false;
        }
        return true;
      }),
    [allPins, category, fulfillment, showSoldOut, withinMiles, centerLat, centerLng, q, producerIndex, assistSellerIds],
  );
  const hiddenCount = allPins.length - pins.length;

  // The card tour: swiping the seller card walks the filtered pins
  // nearest-first from the device fix, panning the map to each -- browsing
  // every maker without hunting individual pins.
  const tourLat = fix?.lat ?? initialCenter?.latitude ?? null;
  const tourLng = fix?.lng ?? initialCenter?.longitude ?? null;
  const tourPins = useMemo(() => {
    if (tourLat == null || tourLng == null) return pins;
    const from = { latitude: tourLat, longitude: tourLng };
    return [...pins].sort(
      (a, b) =>
        haversineMiles(from, { latitude: a.lat, longitude: a.lng }) -
        haversineMiles(from, { latitude: b.lat, longitude: b.lng }),
    );
  }, [pins, tourLat, tourLng]);
  const tourIndex = selected ? tourPins.findIndex((pin) => pin.id === selected.id) : -1;
  // Zoom past the browse framing so there is no doubt which pin is selected.
  const focusPin = (pin: MapPin) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.setView([pin.lat, pin.lng], Math.max(map.getZoom(), 13), { animate: true });
  };
  const goToTour = (index: number) => {
    const pin = tourPins[index];
    if (!pin) return;
    setSelected(pin);
    focusPin(pin);
  };
  const prevTour = tourIndex > 0 ? tourPins[tourIndex - 1] : null;
  const nextTour =
    tourIndex >= 0 && tourIndex < tourPins.length - 1 ? tourPins[tourIndex + 1] : null;
  const {
    swipe: cardSwipe,
    swipeBox: cardBox,
    incoming: cardIncoming,
    touchHandlers: cardTouchHandlers,
    rootRef: cardSwipeRef,
  } = useSwipePager({
    prev: prevTour ? { id: prevTour.id, name: prevTour.displayName } : null,
    next: nextTour ? { id: nextTour.id, name: nextTour.displayName } : null,
    resetKey: selected?.id,
    onCommit: (target) => {
      const index = tourPins.findIndex((pin) => pin.id === target.id);
      if (index >= 0) goToTour(index);
    },
  });
  const incomingPin = cardIncoming
    ? tourPins.find((pin) => pin.id === cardIncoming.id) ?? null
    : null;
  // The card's "mi away" must agree with the tour order beside it, so both
  // measure from the same centre -- the live fix, not the saved one.
  const tourFrom =
    tourLat != null && tourLng != null ? { latitude: tourLat, longitude: tourLng } : null;
  const closeCard = () => {
    setSelected(null);
    const map = mapInstanceRef.current;
    // Selection zoomed in; closing steps well back out so the tour context
    // returns (one level read as barely moving).
    if (map && map.getZoom() >= 13) map.zoomOut(2);
  };

  // A filter that hides the selected pin must not leave the card pointing at
  // an invisible marker -- hand it to the nearest maker who still passes, or
  // close it when nobody does.
  useEffect(() => {
    if (!selected) return;
    if (pins.some((pin) => pin.id === selected.id)) return;
    let next: MapPin | null = null;
    let best = Infinity;
    for (const pin of pins) {
      const miles = haversineMiles(
        { latitude: selected.lat, longitude: selected.lng },
        { latitude: pin.lat, longitude: pin.lng },
      );
      if (Number.isFinite(miles) && miles < best) {
        best = miles;
        next = pin;
      }
    }
    if (next) {
      setSelected(next);
      focusPin(next);
    } else {
      setSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, selected]);

  // Warm both neighbours so the card sliding in already shows their listings.
  const queryClient = useQueryClient();
  useEffect(() => {
    for (const sibling of [prevTour, nextTour]) {
      if (!sibling) continue;
      queryClient.prefetchQuery({
        queryKey: ["/api/users", sibling.id, "items"],
        queryFn: async () => {
          const res = await fetch(`/api/users/${sibling.id}/items`, { credentials: "include" });
          if (!res.ok) throw new Error("Failed to load listings");
          return res.json();
        },
        staleTime: 30_000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevTour?.id, nextTour?.id, queryClient]);

  // Profile swiping follows whatever surface the buyer navigated out of; a
  // tap-through from the map hands the profile page this distance order.
  const publishTourOrder = () => {
    try {
      window.sessionStorage.setItem(
        "stoop.producerSwipeOrder",
        JSON.stringify(tourPins.map((pin) => ({ id: pin.id, name: pin.displayName }))),
      );
    } catch { /* private mode */ }
    // Navigating out pushes the destination on top of this entry, and back
    // pops straight to it -- re-assert the final filters on its URL now so the
    // map reopens exactly as it was left, whoever wrote the URL in between.
    try {
      const final = filtersRef.current;
      const params = new URLSearchParams(window.location.search);
      const setOrDelete = (key: string, value: string | null) => {
        if (value == null) params.delete(key);
        else params.set(key, value);
      };
      setOrDelete("category", final.category);
      setOrDelete("fulfillment", final.fulfillment === "any" ? null : final.fulfillment);
      setOrDelete("showSoldOut", final.showSoldOut ? null : "0");
      setOrDelete("within", final.withinMiles == null ? null : String(final.withinMiles));
      setOrDelete("ebt", final.ebtOnly ? "1" : null);
      setOrDelete("q", final.q || null);
      const qs = params.toString();
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${qs ? `?${qs}` : ""}`,
      );
    } catch { /* the entry keeps its last mirrored URL */ }
  };


  // Unfiltered: "is anyone actually near me" is a fact about the area, not
  // about the filters currently applied. The nearest distance travels with it
  // because the map is framed to keep 25 miles in view, not to clip to it — so
  // pins further out are on screen, and saying "none within 25 miles" without
  // naming the real nearest would read as the map contradicting itself.
  const { nearbyCount, nearestMiles } = useMemo(() => {
    if (centerLat == null || centerLng == null) return { nearbyCount: null, nearestMiles: null };
    let near = 0;
    let nearest: number | null = null;
    for (const pin of allPins) {
      const miles = haversineMiles(
        { latitude: centerLat, longitude: centerLng },
        { latitude: pin.lat, longitude: pin.lng },
      );
      if (!Number.isFinite(miles)) continue;
      if (miles <= OPEN_RADIUS_MILES) near += 1;
      if (nearest == null || miles < nearest) nearest = miles;
    }
    return { nearbyCount: near, nearestMiles: nearest };
  }, [allPins, centerLat, centerLng]);

  // Fetched as soon as the empty state is on screen, not on tap: the share
  // sheet needs the gesture that opened it, and awaiting a fetch first spends
  // it (trap #11).
  const { data: referral } = useQuery<{ shareUrl: string }>({
    queryKey: ["/api/my/referral-code"],
    enabled: nearbyCount === 0,
    queryFn: async () => {
      const res = await fetch("/api/my/referral-code", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load invite link");
      return res.json();
    },
    staleTime: 5 * 60_000,
  });

  const inviteAMaker = () => {
    if (!referral?.shareUrl) {
      toast({ description: "Still loading your invite link. Try again in a second." });
      return;
    }
    // role=maker swaps the /install landing to a "come sell" message instead of
    // the buyer's first-order discount, since this button recruits makers.
    const url = `${referral.shareUrl}&role=maker`;
    const text = `There's nothing on Stoop near us yet. Local food straight from the people who grow and make it. If you sell anything, come list it. ${url}`;
    if (typeof navigator !== "undefined" && navigator.share) {
      void navigator.share({ title: "Stoop", text }).catch(() => {});
      return;
    }
    void navigator.clipboard
      ?.writeText(text)
      .then(() =>
        toast({
          title: "Invite copied",
          description: "Paste it into a text to any maker you know so they can list their food.",
        }),
      )
      .catch(() => {});
  };

  const { data: sellerItems = [] } = useQuery<SellerItem[]>({
    queryKey: ["/api/users", selected?.id, "items"],
    enabled: !!selected,
    queryFn: async () => {
      const res = await fetch(`/api/users/${selected!.id}/items`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load listings");
      return res.json();
    },
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!mapRef.current) return;
    if (!mapInstanceRef.current) {
      // Open already near the user (or first pin) at neighborhood zoom so
      // we never render the whole-world view — that flooded the WebView
      // with world tiles and looked frozen for 20-40s before fitBounds.
      const startCenter: [number, number] = fix
        ? [fix.lat, fix.lng]
        : pins.length > 0
          ? [pins[0].lat, pins[0].lng]
          : [39.5, -98.35];
      const startZoom = fix || pins.length > 0 ? 11 : 4;
      mapInstanceRef.current = L.map(mapRef.current, {
        // Leaflet's default zoom control sits top-left, directly under the
        // filter row. Move it to bottom-left, which is otherwise empty (the
        // exit button is bottom-right).
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
        center: startCenter,
        zoom: startZoom,
      });
      const zoom = L.control.zoom({ position: "bottomleft" });
      zoom.addTo(mapInstanceRef.current);
      // Lift it clear of the home indicator / safe area.
      const zoomEl = zoom.getContainer();
      if (zoomEl) zoomEl.style.marginBottom = "calc(env(safe-area-inset-bottom, 0px) + 14px)";
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(
        mapInstanceRef.current,
      );
    }
    const map = mapInstanceRef.current;

    for (const l of layersRef.current) l.remove();
    layersRef.current = [];

    // Per-seller pin: their avatar in a circular badge (Airbnb host-pin
    // style), with an initial fallback when there's no profile photo.
    const makePinIcon = (pin: MapPin) => {
      const safeUrl = (pin.profileImageUrl || "").replace(/["'<>]/g, "");
      const initial = (pin.displayName || "?").trim().slice(0, 1).toUpperCase();
      const inner = safeUrl
        ? `background-image:url('${safeUrl}');background-size:cover;background-position:center`
        : "background:#A44A2A;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:15px";
      return L.divIcon({
        className: "",
        html: `<div style="width:36px;height:36px;border-radius:50%;border:2.5px solid white;box-shadow:0 2px 7px rgba(0,0,0,0.4);overflow:hidden;${inner}">${safeUrl ? "" : initial}</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    };

    // Draw the search radius so "within N miles" is visible rather than just
    // implied by which pins survived.
    if (filters.withinMiles != null && initialCenter) {
      const ring = L.circle([initialCenter.latitude, initialCenter.longitude], {
        radius: filters.withinMiles * 1609.34,
        color: "#A44A2A",
        weight: 1.5,
        dashArray: "6 6",
        fill: false,
        interactive: false,
      }).addTo(map);
      layersRef.current.push(ring);
    }

    const points: L.LatLng[] = [];
    for (const pin of pins) {
      const open = (e?: L.LeafletMouseEvent) => {
        if (e) L.DomEvent.stopPropagation(e);
        setSelected(pin);
        focusPin(pin);
      };
      const circle = L.circle([pin.lat, pin.lng], {
        radius: pin.radiusMeters,
        color: "#A44A2A",
        weight: 1.5,
        fillColor: "#A44A2A",
        fillOpacity: 0.18,
        interactive: true,
        bubblingMouseEvents: false,
      })
        .addTo(map)
        .on("click", open);
      // Center icon — the primary tap target. Markers have a real DOM hit
      // area, more reliable on touch than an SVG circle path.
      const marker = L.marker([pin.lat, pin.lng], { icon: makePinIcon(pin), riseOnHover: true })
        .addTo(map)
        .on("click", open);
      // Belt-and-suspenders: Leaflet's synthetic click can be eaten by the
      // WebView's touch handling. Bind a native listener on the marker's
      // DOM element too, with a tap-vs-drag guard so panning doesn't open
      // the card. Either path opens it.
      const el = marker.getElement() as HTMLElement | null;
      if (el) {
        el.style.cursor = "pointer";
        let sx = 0;
        let sy = 0;
        el.addEventListener("touchstart", (ev) => {
          const t = ev.touches[0];
          sx = t.clientX;
          sy = t.clientY;
        }, { passive: true });
        el.addEventListener("touchend", (ev) => {
          const t = ev.changedTouches[0];
          if (Math.abs(t.clientX - sx) < 12 && Math.abs(t.clientY - sy) < 12) {
            // preventDefault suppresses the synthetic click so the card opens
            // once. Do NOT stopPropagation: Leaflet clears its global drag flag
            // (Draggable._dragging) on the document-level touchend, and stopping
            // it here strands that flag with the origin frozen at this pin — the
            // next drag then snaps the pin under the finger. Verified via an
            // isolated Leaflet touch repro.
            ev.preventDefault();
            setSelected(pin);
            focusPin(pin);
          }
        });
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          setSelected(pin);
          focusPin(pin);
        });
      }
      layersRef.current.push(circle, marker);
      points.push(L.latLng(pin.lat, pin.lng));
    }

    // The viewer's own position, drawn only for a real device fix. Leaflet has
    // no locate marker of its own, so this is the familiar blue dot plus the
    // reported accuracy as a soft halo.
    if (fix && fix.source === "gps") {
      if (fix.accuracy != null && fix.accuracy > 25) {
        layersRef.current.push(
          L.circle([fix.lat, fix.lng], {
            radius: fix.accuracy,
            color: "#2563eb",
            weight: 1,
            fillColor: "#2563eb",
            fillOpacity: 0.1,
            interactive: false,
          }).addTo(map),
        );
      }
      layersRef.current.push(
        L.circleMarker([fix.lat, fix.lng], {
          radius: 7,
          color: "#ffffff",
          weight: 3,
          fillColor: "#2563eb",
          fillOpacity: 1,
          interactive: false,
        }).addTo(map),
      );
    }

    // Open on the viewer at roughly OPEN_RADIUS_MILES, rather than fitting
    // every pin in the country. If nobody is that close, widen to take in the
    // nearest few makers — an empty map is worse than a wide one.
    //
    // Re-frame when the fix gets better, not just once: the map opens on the
    // saved centre because that is available synchronously, and the real fix
    // lands a second or two later. Framing only on first paint would leave
    // everyone looking at wherever they were last time. A deliberate pan wins
    // over both.
    const fixRank: Record<FixSource, number> = { saved: 0, approx: 1, gps: 2 };
    const improved = fix != null && fixRank[fix.source] > framedRankRef.current;
    if (fix && (!didFitRef.current || (improved && !userMovedRef.current))) {
      framedRankRef.current = fixRank[fix.source];
      const here = L.latLng(fix.lat, fix.lng);
      const nearby = points.filter(
        (pt) => here.distanceTo(pt) <= OPEN_RADIUS_MILES * METERS_PER_MILE,
      );
      if (nearby.length > 0) {
        // Frame the makers who are actually nearby rather than the whole
        // 25-mile circle -- the circle opened wildly zoomed out whenever the
        // real cluster sat within a few miles.
        map.fitBounds(L.latLngBounds([here, ...nearby]), { padding: [40, 40], maxZoom: 13 });
      } else if (points.length === 0) {
        map.fitBounds(here.toBounds(OPEN_RADIUS_MILES * 2 * METERS_PER_MILE), {
          padding: [24, 24],
        });
      } else {
        const nearest = [...points]
          .sort((a, b) => here.distanceTo(a) - here.distanceTo(b))
          .slice(0, 3);
        map.fitBounds(L.latLngBounds([here, ...nearest]), { padding: [40, 40], maxZoom: 12 });
      }
      didFitRef.current = true;
      refitPendingRef.current = false;
    } else if (points.length > 0 && (!didFitRef.current || refitPendingRef.current)) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
      didFitRef.current = true;
      refitPendingRef.current = false;
    }

    // The map mounts inside a just-opened fixed overlay; Leaflet often
    // measures the container at the wrong size before layout settles,
    // which leaves tap coordinates offset so circle/pin taps miss. Recompute
    // size on the next frames so hit-testing lines up.
    requestAnimationFrame(() => {
      if (mapInstanceRef.current === map) map.invalidateSize();
    });
    const t = setTimeout(() => {
      if (mapInstanceRef.current === map) map.invalidateSize();
    }, 250);
    return () => clearTimeout(t);
  }, [pins, fix]);

  // Opened from a maker's page: start on their pin (softer than the selection
  // zoom) and pin the framing there -- the improving-fix reframe would
  // otherwise snatch the view back to the buyer a second later.
  const focusedSellerRef = useRef(false);
  useEffect(() => {
    if (!focusSellerId || focusedSellerRef.current) return;
    const pin = allPins.find((p) => p.id === focusSellerId);
    const map = mapInstanceRef.current;
    if (!pin || !map) return;
    focusedSellerRef.current = true;
    didFitRef.current = true;
    framedRankRef.current = 2;
    map.setView([pin.lat, pin.lng], 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPins, focusSellerId]);

  // Track deliberate panning so a late fix doesn't snatch the view back.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const onDrag = () => {
      userMovedRef.current = true;
    };
    map.on("dragstart", onDrag);
    return () => {
      map.off("dragstart", onDrag);
    };
  }, [pins]);

  const recenter = useCallback(async () => {
    userMovedRef.current = false;
    framedRankRef.current = -1;
    const ok = await locate();
    if (ok) return;
    // Location refused: still put them back where we think they are rather than
    // leaving the button feeling dead.
    const map = mapInstanceRef.current;
    if (map && fix) {
      map.fitBounds(L.latLng(fix.lat, fix.lng).toBounds(OPEN_RADIUS_MILES * 2 * METERS_PER_MILE), {
        padding: [24, 24],
      });
    }
  }, [locate, fix]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        // Halt any in-flight pan/zoom animation first: unmounting mid-animation
        // otherwise leaves a queued frame calling getPosition on the removed
        // pane, throwing "Cannot read properties of undefined (_leaflet_pos)".
        mapInstanceRef.current.stop();
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Center the map on the selected maker's pin with a light slide (fires for any
  // of the pin's click/touch handlers, since they all set `selected`). A short
  // 0.25s pan: an isolated touch repro showed the residual snap from dragging
  // mid-animation is negligible at any realistic drag timing (~200ms+ after the
  // tap) and stays tiny even on an unrealistically fast drag.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selected) return;
    map.panTo([selected.lat, selected.lng], { animate: true, duration: 0.25 });
  }, [selected]);

  return (
    <div className="fixed inset-0 z-[60] bg-background">
      {/* z-0 makes the map its own stacking context so Leaflet's internal
          panes (z-index up to 1000) stay contained and don't paint over
          the seller card / close button below. Without this the card opens
          but renders behind the map and looks like nothing happened. */}
      <div ref={mapRef} className="absolute inset-0 z-0" />

      {isLoading && (
        <div className="pointer-events-none absolute inset-0 z-[61] flex items-center justify-center">
          <span className="rounded-full bg-background/90 px-4 py-2 text-sm font-medium text-muted-foreground shadow">
            Loading map…
          </span>
        </div>
      )}

      {/* Filter row, scrollable so it never wraps on a narrow screen. Nothing
          else lives up here now — the map's exit button sits bottom-right. */}
      <div
        className="absolute inset-x-0 z-[61] flex gap-1.5 overflow-x-auto px-3 pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 10px)" }}
      >
        {/* Leads the row while anything is narrowing the map, matching the chip
            on the browse filter row. Clearing here clears browse too — these are
            one shared selection — so a map you've filtered down to nothing is
            always one tap from whole again. */}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-primary bg-primary px-2.5 text-xs font-medium text-primary-foreground shadow"
            data-testid="map-clear-filters"
          >
            <span>
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
            </span>
            <X className="h-3 w-3" />
          </button>
        )}

        {q !== "" && (
          <button
            type="button"
            onClick={() => updateFilters({ q: "" })}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-primary bg-primary px-2.5 text-xs font-medium text-primary-foreground shadow"
            data-testid="map-clear-search"
          >
            <span className="max-w-[8rem] truncate">{q}</span>
            <X className="h-3 w-3" />
          </button>
        )}

        {/* Search straight from the map: pill when idle, inline input while
            typing. Applies through the same shared filters as everything else. */}
        {q === "" &&
          (mapSearchOpen ? (
            <input
              autoFocus
              placeholder="Search makers or products"
              className="h-7 w-44 shrink-0 rounded-full border border-border bg-background px-2.5 text-xs shadow outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = e.currentTarget.value.trim();
                  setMapSearchOpen(false);
                  if (v) updateFilters({ q: v });
                } else if (e.key === "Escape") {
                  setMapSearchOpen(false);
                }
              }}
              onBlur={(e) => {
                const v = e.target.value.trim();
                setMapSearchOpen(false);
                if (v) updateFilters({ q: v });
              }}
              data-testid="map-search-input"
            />
          ) : (
            <button
              type="button"
              onClick={() => setMapSearchOpen(true)}
              className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-border bg-background px-2.5 text-xs font-medium text-foreground shadow"
              data-testid="map-search-open"
            >
              <Search className="h-3 w-3" />
              Search
            </button>
          ))}

        <select
          value={withinMiles == null ? "any" : String(withinMiles)}
          onChange={(e) =>
            updateFilters({ withinMiles: e.target.value === "any" ? null : Number(e.target.value) })
          }
          className={`h-7 shrink-0 rounded-full border px-2.5 text-xs font-medium shadow ${withinMiles == null ? "border-border bg-background text-foreground" : "border-primary bg-primary text-primary-foreground"}`}
          data-testid="map-filter-distance"
        >
          <option value="any">Any distance</option>
          {DISTANCE_OPTIONS.map((mi) => (
            <option key={mi} value={String(mi)}>
              Within {mi} mi
            </option>
          ))}
        </select>

        <select
          value={category ?? "any"}
          onChange={(e) => updateFilters({ category: e.target.value === "any" ? null : e.target.value })}
          className={`h-7 shrink-0 rounded-full border px-2.5 text-xs font-medium capitalize shadow ${category == null ? "border-border bg-background text-foreground" : "border-primary bg-primary text-primary-foreground"}`}
          data-testid="map-filter-category"
        >
          <option value="any">All categories</option>
          {BROWSE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={fulfillment}
          onChange={(e) => updateFilters({ fulfillment: e.target.value as BrowseFilters["fulfillment"] })}
          className={`h-7 shrink-0 rounded-full border px-2.5 text-xs font-medium shadow ${fulfillment === "any" ? "border-border bg-background text-foreground" : "border-primary bg-primary text-primary-foreground"}`}
          data-testid="map-filter-fulfillment"
        >
          <option value="any">Any fulfillment</option>
          <option value="seller_pickup">Pickup</option>
          <option value="drop_zone_pickup">Hub</option>
          <option value="local_delivery">Delivery</option>
        </select>

        <button
          type="button"
          onClick={() => updateFilters({ showSoldOut: !showSoldOut })}
          className={`h-7 shrink-0 rounded-full border px-2.5 text-xs font-medium shadow ${showSoldOut ? "border-border bg-background text-foreground" : "border-primary bg-primary text-primary-foreground"}`}
          data-testid="map-filter-soldout"
        >
          {showSoldOut ? "All makers" : "In stock"}
        </button>
      </div>

      <div
        className="pointer-events-none absolute left-1/2 z-[61] flex -translate-x-1/2 flex-col items-center gap-1"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 48px)" }}
      >
        <span className="rounded-full bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow">
          Approximate areas · tap a circle
        </span>
        {/* Without this a filtered map just looks empty, and the maker who
            isn't showing up reads as a bug rather than a filter. */}
        {hiddenCount > 0 && (
          <span className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow">
            {withinMiles != null ? `Within ${withinMiles} mi · ` : ""}
            {pins.length} shown, {hiddenCount} filtered out
          </span>
        )}
        {withinMiles != null && centerLat == null && (
          <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-medium text-amber-950 shadow">
            Set your location to filter by distance
          </span>
        )}
        {fix?.source === "approx" && (
          <span className="rounded-full bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow">
            Approximate location · turn on GPS for exact
          </span>
        )}
      </div>

      {/* Back to me. Sits above the exit pill in the same bottom-right column. */}
      <button
        type="button"
        onClick={() => void recenter()}
        aria-label="Center on my location"
        className="absolute right-3 z-[61] flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background shadow-lg"
        style={{
          // The seller card paints over this corner (same z, later in the DOM),
          // and with the card tour open the card is up most of the time -- ride
          // above it or the button is dead exactly when people use the map most.
          // The exit pill rides at sheetH + 12 and is ~40px tall; stack above
          // it, not on it.
          bottom:
            sheetH > 0
              ? `${Math.round(sheetH) + 64}px`
              : "calc(env(safe-area-inset-bottom, 0px) + 132px)",
        }}
        data-testid="map-recenter"
      >
        <LocateFixed className={`h-5 w-5 ${locating ? "animate-pulse text-muted-foreground" : "text-primary"}`} />
      </button>

      {/* Location is off or refused. Dismissible, and the map still works from
          the approximate centre — Apple requires the app stay usable without
          location, and a wall here would also be the wrong trade for someone
          who keeps GPS off deliberately. No settings deep-link: that needs a
          native plugin, which needs a new binary (trap #21). */}
      {gpsProblem && !promptDismissed && (
        <div className="absolute inset-x-3 z-[62] rounded-2xl border border-border bg-background p-4 shadow-2xl" style={{ top: "calc(env(safe-area-inset-top, 0px) + 96px)" }}>
          <h3 className="text-sm font-semibold">
            {gpsProblem === "denied" ? "Location is turned off for Stoop" : "We can't get your location"}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {gpsProblem === "denied"
              ? "Allow location for Stoop in your device settings and we'll show exactly who's closest to you."
              : "Turn on Location Services in your device settings, then try again. Until then we're showing your rough area."}
          </p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => void locate()} disabled={locating}>
              {locating ? "Checking…" : "Try again"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPromptDismissed(true)}>
              Not now
            </Button>
          </div>
        </div>
      )}

      {/* Nobody within the opening radius. The honest reading of an empty map
          is "we haven't reached your block yet", and the one thing that fixes
          that is the person looking at it. */}
      {!isLoading && fix && nearbyCount === 0 && !selected && !emptyDismissed && (
        <div
          className="absolute inset-x-3 z-[61] touch-pan-y rounded-2xl border border-border bg-background/95 p-4 shadow-2xl"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
            transform: emptySwipe ? `translateX(${emptySwipe}px)` : undefined,
            opacity: emptySwipe ? Math.max(0, 1 - Math.abs(emptySwipe) / 220) : undefined,
            transition: emptyTouchStart.current != null ? "none" : "transform 0.2s, opacity 0.2s",
          }}
          onTouchStart={(e) => {
            emptyTouchStart.current = e.touches[0].clientX;
          }}
          onTouchMove={(e) => {
            if (emptyTouchStart.current == null) return;
            setEmptySwipe(e.touches[0].clientX - emptyTouchStart.current);
          }}
          onTouchEnd={() => {
            if (Math.abs(emptySwipe) > 110) setEmptyDismissed(true);
            emptyTouchStart.current = null;
            setEmptySwipe(0);
          }}
        >
          <button
            type="button"
            onClick={() => setEmptyDismissed(true)}
            aria-label="Dismiss"
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
          <h3 className="pr-7 text-sm font-semibold">No makers within {OPEN_RADIUS_MILES} miles yet</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {nearestMiles != null
              ? `The nearest is about ${Math.round(nearestMiles)} miles away. `
              : ""}
            You're early here. Know someone nearby who grows, bakes or makes something? An invite
            from a neighbor is how every one of these pins started.
          </p>
          <Button size="sm" className="mt-3 w-full" onClick={() => void inviteAMaker()}>
            Invite a maker near you
          </Button>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            When a maker you invite posts their first listing, you get 60% off a Stoop service fee.
            Swipe this card away to hide it for now.
          </p>
        </div>
      )}

      {selected && (
        <div
          ref={(el) => {
            sheetRef.current = el;
            cardSwipeRef.current = el;
          }}
          className="absolute inset-x-0 bottom-0 z-[61] touch-pan-y rounded-t-2xl border-t border-border bg-background p-4 shadow-2xl"
          style={
            cardSwipe.dx !== 0 || cardSwipe.animating
              ? {
                  transform: `translate3d(${cardSwipe.dx}px, 0, 0)`,
                  transition: cardSwipe.animating ? "transform 180ms ease-out" : "none",
                  willChange: "transform",
                }
              : undefined
          }
          {...cardTouchHandlers}
        >
          <SellerTourCard
            pin={selected}
            items={sellerItems}
            index={tourIndex}
            count={tourPins.length}
            from={tourFrom}
            onPrev={() => goToTour(tourIndex - 1)}
            onNext={() => goToTour(tourIndex + 1)}
            onClose={closeCard}
            onProfileClick={() => {
              publishTourOrder();
              onNavigate?.();
            }}
            onItemClick={() => {
              publishTourOrder();
              onNavigate?.();
            }}
          />
        </div>
      )}

      {incomingPin && cardBox.width > 0 && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[62] touch-pan-y rounded-t-2xl border-t border-border bg-background p-4 shadow-2xl"
          style={{
            transform: `translate3d(${
              cardSwipe.dx + (cardIncoming!.fromRight ? cardBox.width : -cardBox.width)
            }px, 0, 0)`,
            transition: cardSwipe.animating ? "transform 180ms ease-out" : "none",
            willChange: "transform",
          }}
          aria-hidden="true"
        >
          <SellerTourCard
            pin={incomingPin}
            items={
              queryClient.getQueryData<SellerItem[]>(["/api/users", incomingPin.id, "items"]) ?? []
            }
            index={tourPins.findIndex((pin) => pin.id === incomingPin.id)}
            count={tourPins.length}
            from={tourFrom}
          />
        </div>
      )}
    </div>
  );
}

function SellerTourCard({
  pin,
  items,
  index,
  count,
  from,
  onPrev,
  onNext,
  onClose,
  onProfileClick,
  onItemClick,
}: {
  pin: MapPin;
  items: SellerItem[];
  index: number;
  count: number;
  from: { latitude: number; longitude: number } | null;
  onPrev?: () => void;
  onNext?: () => void;
  onClose?: () => void;
  onProfileClick?: () => void;
  onItemClick?: () => void;
}) {
  return (
    <>
      {index >= 0 && count > 1 && (
        <div className="mb-2 flex items-center justify-center gap-3 text-[11px] text-muted-foreground">
          <button
            type="button"
            onClick={onPrev}
            disabled={index <= 0}
            className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-muted disabled:opacity-30"
            aria-label="Closer maker"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span>
            {index + 1} of {count} · nearest first · swipe for more
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={index >= count - 1}
            className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-muted disabled:opacity-30"
            aria-label="Next maker out"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-3">
        {pin.profileImageUrl ? (
          <img
            src={pin.profileImageUrl}
            alt={pin.displayName}
            className="h-11 w-11 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-muted-foreground">
            {pin.displayName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <Link
            href={`/u/${pin.id}`}
            onClick={onProfileClick}
            className="flex items-center gap-1 font-display text-base font-bold text-foreground hover:underline"
          >
            {pin.displayName}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {pin.reviewCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Star className="h-3 w-3 fill-current text-amber-500" />
                {pin.averageRating.toFixed(1)} ({pin.reviewCount})
              </span>
            )}
            {(pin.city || pin.state) && (
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="h-3 w-3" />
                {[pin.city, pin.state].filter(Boolean).join(", ")}
              </span>
            )}
            {from && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
                <Navigation className="h-3 w-3" />
                ~{Math.max(
                  1,
                  Math.round(
                    haversineMiles(from, { latitude: pin.lat, longitude: pin.lng }),
                  ),
                )}{" "}
                mi away
              </span>
            )}
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close seller card"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
        {items.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">No active listings.</p>
        ) : (
          items.slice(0, 12).map((item) => (
            <Link
              key={item.id}
              href={`/items/${item.id}`}
              onClick={onItemClick}
              className="w-28 shrink-0"
              data-testid="map-listing"
            >
              <div className="aspect-square w-28 overflow-hidden rounded-lg bg-muted">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
                ) : null}
              </div>
              <p className="mt-1 truncate text-xs font-medium text-foreground">{item.name}</p>
              <p className="text-xs text-muted-foreground">{money(item.price)}</p>
            </Link>
          ))
        )}
      </div>
    </>
  );
}
